"use client";

import * as React from "react";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import * as SelectPrimitive from "@radix-ui/react-select";

import { cn } from "lib/utils";

const Select = SelectPrimitive.Select;

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.SelectTrigger> & {
  size?: "sm" | "default";
}) {
  return (
    <SelectPrimitive.SelectTrigger
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-md border border-[#EEEEEE] bg-white px-3 py-2 text-xs font-normal leading-5 shadow-none transition-colors",
        "focus:border-[#222222] focus:outline-none focus:ring-1 focus:ring-[#222222]",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "[&>span]:line-clamp-1",
        size === "default" ? "h-9 min-h-[36px]" : "h-8",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.SelectIcon asChild>
        <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
      </SelectPrimitive.SelectIcon>
    </SelectPrimitive.SelectTrigger>
  );
}

function SelectContent({
  className,
  children,
  position = "item-aligned",
  align = "center",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.SelectContent>) {
  return (
    <SelectPrimitive.SelectPortal>
      <SelectPrimitive.SelectContent
        className={cn(
          "relative z-50 max-h-[var(--radix-select-content-available-height)] min-w-[8rem] overflow-hidden rounded-md border border-[#EEEEEE] bg-white shadow-md",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        align={align}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.SelectViewport className="p-1">
          {children}
        </SelectPrimitive.SelectViewport>
        <SelectScrollDownButton />
      </SelectPrimitive.SelectContent>
    </SelectPrimitive.SelectPortal>
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.SelectScrollUpButton>) {
  return (
    <SelectPrimitive.SelectScrollUpButton
      className={cn("flex cursor-default items-center justify-center py-1", className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.SelectScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.SelectScrollDownButton>) {
  return (
    <SelectPrimitive.SelectScrollDownButton
      className={cn("flex cursor-default items-center justify-center py-1", className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.SelectScrollDownButton>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.SelectLabel>) {
  return (
    <SelectPrimitive.SelectLabel
      className={cn("px-2 py-1.5 text-xs text-[#6D6D6D]", className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.SelectItem>) {
  return (
    <SelectPrimitive.SelectItem
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pr-8 pl-2 text-xs outline-none",
        "focus:bg-[#F7F7F7] focus:text-[#222222]",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.SelectItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.SelectItemIndicator>
      </span>
      <SelectPrimitive.SelectItemText>{children}</SelectPrimitive.SelectItemText>
    </SelectPrimitive.SelectItem>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.SelectSeparator>) {
  return (
    <SelectPrimitive.SelectSeparator
      className={cn("-mx-1 my-1 h-px bg-[#EEEEEE]", className)}
      {...props}
    />
  );
}

const SelectGroup = SelectPrimitive.SelectGroup;
const SelectValue = SelectPrimitive.SelectValue;

/** Reusable labeled Select for forms - matches the SelectField API used across the app */
function SelectField({
  label,
  value,
  onChange,
  placeholder = "Select an option",
  options = [],
  className,
  labelClassName,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  options?: { value: string; label: string }[];
  className?: string;
  labelClassName?: string;
}) {
  return (
    <div className={cn("flex flex-1 min-w-0 flex-col gap-1", className)}>
      <label className={cn("text-xs font-normal leading-5 text-[#222222]", labelClassName)}>
        {label}
      </label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export {
  Select,
  SelectContent,
  SelectField,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
